const MS_PER_DAY = 86400000

export function addDays (d, n) {
  const r = new Date(d)
  r.setDate(r.getDate() + n)
  return r
}

export function startOfWeek (d) {
  const r = new Date(d)
  const day = r.getDay()
  const diff = day === 0 ? 6 : day - 1
  r.setDate(r.getDate() - diff)
  return r
}

export function startOfMonth (d) {
  return new Date(d.getFullYear(), d.getMonth(), 1)
}

export function endOfMonth (d) {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0)
}

export function toDateKey (d) {
  const y = d.getFullYear()
  const m = ('0' + (d.getMonth() + 1)).slice(-2)
  const day = ('0' + d.getDate()).slice(-2)
  return `${y}-${m}-${day}`
}

export function parseDateKey (key) {
  const [y, m, d,] = key.split('-').map(Number)
  return new Date(y, m - 1, d)
}

export function daysBetween (a, b) {
  return Math.round((b - a) / MS_PER_DAY)
}

function fiscalRange (availableDates, reportDateKey, fieldKey) {
  const reportEntry = availableDates.find(d => d.date_key === reportDateKey)
  if (!reportEntry) return null
  const targetKey = reportEntry[fieldKey]
  const yearKey = reportEntry.year_key
  const matches = availableDates.filter(
    d => d.year_key === yearKey && d[fieldKey] === targetKey
  )
  if (!matches.length) return null
  return [parseDateKey(matches[0].date_key), parseDateKey(matches[matches.length - 1].date_key),]
}

function fiscalRangeTD (availableDates, reportDateKey, fieldKey) {
  const reportEntry = availableDates.find(d => d.date_key === reportDateKey)
  if (!reportEntry) return null
  const targetKey = reportEntry[fieldKey]
  const yearKey = reportEntry.year_key
  const matches = availableDates.filter(
    d => d.year_key === yearKey && d[fieldKey] === targetKey && d.date_key <= reportDateKey
  )
  if (!matches.length) return null
  return [parseDateKey(matches[0].date_key), parseDateKey(matches[matches.length - 1].date_key),]
}

function fiscalYearRange (availableDates, reportDateKey) {
  const reportEntry = availableDates.find(d => d.date_key === reportDateKey)
  if (!reportEntry) return null
  const yearKey = reportEntry.year_key
  const matches = availableDates.filter(d => d.year_key === yearKey)
  if (!matches.length) return null
  return [parseDateKey(matches[0].date_key), parseDateKey(matches[matches.length - 1].date_key),]
}

function fiscalYearTD (availableDates, reportDateKey) {
  const reportEntry = availableDates.find(d => d.date_key === reportDateKey)
  if (!reportEntry) return null
  const yearKey = reportEntry.year_key
  const matches = availableDates.filter(
    d => d.year_key === yearKey && d.date_key <= reportDateKey
  )
  if (!matches.length) return null
  return [parseDateKey(matches[0].date_key), parseDateKey(matches[matches.length - 1].date_key),]
}

export function computePresetRange (key, reportDate, availableDates) {
  const d = new Date(reportDate)
  const dk = toDateKey(d)
  switch (key) {
    case 'current_day':
      return [d, d,]
    case 'previous_day':
      return [addDays(d, -1), addDays(d, -1),]
    case 'current_week':
      return fiscalRange(availableDates, dk, 'week_key') ||
        [startOfWeek(d), addDays(startOfWeek(d), 6),]
    case 'wtd':
      return fiscalRangeTD(availableDates, dk, 'week_key') ||
        [startOfWeek(d), d,]
    case 'previous_week': {
      const prevDay = addDays(startOfWeek(d), -1)
      const prevDk = toDateKey(prevDay)
      return fiscalRange(availableDates, prevDk, 'week_key') ||
        (() => { const ps = addDays(startOfWeek(d), -7); return [ps, addDays(ps, 6),] })()
    }
    case 'trailing_7':
      return [addDays(d, -6), d,]
    case 'next_week': {
      const nextDay = addDays(startOfWeek(d), 7)
      const nextDk = toDateKey(nextDay)
      return fiscalRange(availableDates, nextDk, 'week_key') ||
        [nextDay, addDays(nextDay, 6),]
    }
    case 'current_month':
      return [startOfMonth(d), endOfMonth(d),]
    case 'mtd':
      return [startOfMonth(d), d,]
    case 'previous_month': {
      const prevMonth = new Date(d.getFullYear(), d.getMonth() - 1, 1)
      return [prevMonth, endOfMonth(prevMonth),]
    }
    case 'current_period':
      return fiscalRange(availableDates, dk, 'period_key')
    case 'ptd':
      return fiscalRangeTD(availableDates, dk, 'period_key')
    case 'current_quarter':
      return fiscalRange(availableDates, dk, 'quarter_key')
    case 'qtd':
      return fiscalRangeTD(availableDates, dk, 'quarter_key')
    case 'current_calendar_year':
      return [new Date(d.getFullYear(), 0, 1), new Date(d.getFullYear(), 11, 31),]
    case 'calendar_ytd':
      return [new Date(d.getFullYear(), 0, 1), d,]
    case 'current_year':
      return fiscalYearRange(availableDates, dk)
    case 'ytd':
      return fiscalYearTD(availableDates, dk)
    case 'all_time': {
      if (!availableDates.length) return null
      return [
        parseDateKey(availableDates[0].date_key),
        parseDateKey(availableDates[availableDates.length - 1].date_key),
      ]
    }
    default:
      return null
  }
}

// For XTD ranges, compute the full unit range (not just to-date)
// to get the max possible duration for overlap warnings
const TD_TO_FULL = {
  wtd: 'current_week',
  mtd: 'current_month',
  ptd: 'current_period',
  qtd: 'current_quarter',
  calendar_ytd: 'current_calendar_year',
  ytd: 'current_year',
}

function maxFiscalUnitDays (availableDates, fieldKey) {
  if (!availableDates.length) return null
  const groups = {}
  availableDates.forEach(d => {
    const groupKey = d.year_key + '|' + d[fieldKey]
    if (!groups[groupKey]) groups[groupKey] = 0
    groups[groupKey]++
  })
  return Math.max(...Object.values(groups))
}

// Map fiscal-dependent keys to their unit field for max calculation
const FISCAL_UNIT_FIELD = {
  current_week: 'week_key',
  wtd: 'week_key',
  current_period: 'period_key',
  ptd: 'period_key',
  current_quarter: 'quarter_key',
  qtd: 'quarter_key',
  current_year: 'year_key',
  ytd: 'year_key',
}

export function computeMaxDurationDays (key, reportDate, availableDates) {
  // For calendar-based ranges, compute the max possible duration
  switch (key) {
    case 'current_day':
    case 'previous_day':
      return 1
    case 'trailing_7':
      return 7
    case 'next_week':
      return 7
    case 'current_month':
    case 'mtd':
    case 'previous_month':
      return 31
    case 'current_calendar_year':
    case 'calendar_ytd':
      return 366
    case 'all_time':
      return availableDates.length || null
    default:
      break
  }

  // For fiscal-dependent ranges, find the longest unit in the calendar
  const unitField = FISCAL_UNIT_FIELD[key]
  if (unitField && availableDates.length) {
    return maxFiscalUnitDays(availableDates, unitField)
  }

  // Fallback: compute from the specific report date
  const fullKey = TD_TO_FULL[key]
  const range = computePresetRange(fullKey || key, reportDate, availableDates)
  if (range) return daysBetween(range[0], range[1]) + 1
  return null
}

export function computeMaxPresetRange (key, reportDate, availableDates) {
  const fullKey = TD_TO_FULL[key]
  if (fullKey) {
    return computePresetRange(fullKey, reportDate, availableDates)
  }
  return computePresetRange(key, reportDate, availableDates)
}
